/**
 * Who leads each buyer-journey stage, on each signal (2026-09-08).
 *
 * The "Leaders by Buyer-Journey Stage" section is a wall of three ranked lists
 * per stage — fifteen short lists in one table. This turns it into a matrix the
 * eye can scan: stages down, signals across, the leader in each cell with a bar
 * for how dominant they are, and the client's own standing called out whenever
 * they are not the leader.
 *
 * Built as an HTML grid rather than SVG because every cell is primarily a NAME;
 * SVG text has no wrapping and company names here are long.
 *
 * FORM: emphasis. One hue per signal column (matching the signal colours used
 * everywhere else in the report), and the client's own bar in that hue while
 * everyone else stays context grey.
 */
import { Fragment } from 'react';
import type { StageSignalLeaders, SignalLeaderEntry } from '@aurora/shared';
import { buyerStageLabel } from '@aurora/shared';
import { CONTEXT, SIGNAL_HUES } from './palette';

const COLUMNS = [
  { key: 'mostMentions', name: 'Mentions', hue: SIGNAL_HUES.mention },
  { key: 'mostCited', name: 'Citations', hue: SIGNAL_HUES.citation },
  {
    key: 'mostRecommended',
    name: 'Recommendations',
    hue: SIGNAL_HUES.recommendation,
  },
] as const;

const pct = (v: number): string => {
  const p = v * 100;
  if (p > 0 && p < 1) return '<1%';
  return `${Math.round(p)}%`;
};

function listFor(
  s: StageSignalLeaders,
  key: (typeof COLUMNS)[number]['key'],
): SignalLeaderEntry[] {
  if (key === 'mostMentions') return s.mostMentions ?? s.mostVisible ?? [];
  return (s[key] as SignalLeaderEntry[] | undefined) ?? [];
}

function Cell({
  list,
  hue,
}: {
  list: SignalLeaderEntry[];
  hue: string;
}): JSX.Element {
  const leader = list[0];
  if (!leader || leader.count === 0) {
    return <div className="leader-cell is-empty">No company led here</div>;
  }
  const you = list.find((e) => e.isTarget);
  const youRank = you ? list.findIndex((e) => e.isTarget) + 1 : 0;
  return (
    <div className="leader-cell">
      <div className={leader.isTarget ? 'leader-name is-target' : 'leader-name'}>
        {leader.company}
        {leader.isTarget ? ' (you)' : ''}
      </div>
      <div className="leader-bar-track">
        <div
          className="leader-bar"
          style={{
            width: `${Math.max(3, Math.min(100, leader.rate * 100))}%`,
            background: leader.isTarget ? hue : CONTEXT,
          }}
        />
      </div>
      <div className="leader-meta">
        {leader.count} {leader.count === 1 ? 'answer' : 'answers'} ·{' '}
        {pct(leader.rate)}
      </div>
      {you && !leader.isTarget && (
        <div className="leader-you">
          You: #{youRank} · {you.count} ({pct(you.rate)})
        </div>
      )}
      {!you && (
        <div className="leader-you is-absent">
          You: not in the top {list.length}
        </div>
      )}
    </div>
  );
}

export function StageLeaderGrid({
  stages,
}: {
  stages: StageSignalLeaders[];
}): JSX.Element | null {
  if (stages.length === 0) return null;
  return (
    <figure className="stage-leader-grid-wrap">
      <div className="stage-leader-grid">
        <div className="leader-corner" />
        {COLUMNS.map((c) => (
          <div key={c.key} className="leader-colhead">
            <span
              className="chart-legend-swatch"
              style={{ background: c.hue }}
            />
            {c.name}
          </div>
        ))}
        {stages.map((s) => (
          <Fragment key={s.stage}>
            <div className="leader-rowhead">{buyerStageLabel(s.stage)}</div>
            {COLUMNS.map((c) => (
              <Cell
                key={c.key}
                list={listFor(s, c.key)}
                hue={c.hue}
              />
            ))}
          </Fragment>
        ))}
      </div>
      <figcaption className="muted ranked-bars-caption">
        The company leading each buyer-journey stage on each signal, with the bar
        showing what share of that stage&apos;s answers they carried. Where you
        are not the leader, your own position is shown beneath.
      </figcaption>
    </figure>
  );
}
